import { SettingsPermissions } from "@redotech/account-common/permissions";
import { Payer } from "@redotech/congo-enums/payer";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { Step } from "@redotech/redo-return-core-common/return-flow";
import { SubmitRepairState } from "@redotech/redo-return-ui/flows/return-flow/submit-repair";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo, useMemo } from "react";
import { AmountType, AmountTypeDropdown } from "./amount-type-dropdown";

function repairFeeModeLabel(
  mode: Step.RepairFeeMode,
  t: (key: string) => string,
): string {
  switch (mode) {
    case Step.RepairFeeMode.Free:
      return t("Free");
    case Step.RepairFeeMode.Instant:
      return t("Instant");
    case Step.RepairFeeMode.Deferred:
      return t("Deferred");
  }
}

function repairFeeModeDescription(
  mode: Step.RepairFeeMode,
  t: (key: string) => string,
): string {
  switch (mode) {
    case Step.RepairFeeMode.Free:
      return t("No repair fee is charged for this item.");
    case Step.RepairFeeMode.Instant:
      return t(
        "A fee is charged when the customer submits the request. If any item on the return has a deferred fee, this amount is added to the deferred invoice instead of being charged at submission.",
      );
    case Step.RepairFeeMode.Deferred:
      return t(
        "The customer sees an estimated fee range now and receives an invoice after the repair is reviewed.",
      );
  }
}

function defaultConfigForMode(mode: Step.RepairFeeMode): Step.RepairFeeConfig {
  switch (mode) {
    case Step.RepairFeeMode.Instant:
      return { strategy: Step.RepairFeeMode.Instant, amount: 0 };
    case Step.RepairFeeMode.Deferred:
      return {
        strategy: Step.RepairFeeMode.Deferred,
        minPrice: 0,
        maxPrice: 0,
      };
    case Step.RepairFeeMode.Free:
      return { strategy: Step.RepairFeeMode.Free };
  }
}

export const SubmitRepairDetails = memo(function SubmitRepairDetails({
  state,
  setState,
}: StepTypeDetailsProps<SubmitRepairState>) {
  const { t } = useTranslation();
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );

  const handleSafetyNet = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.labelPayer = value ? Payer.Merchant : Payer.Customer;
      }),
    );
  });

  const repairFeeModeItems = useMemo(
    () =>
      Object.values(Step.RepairFeeMode).map((mode) => ({
        id: mode,
        value: mode,
        type: "text" as const,
        text: repairFeeModeLabel(mode, t),
      })),
    [t],
  );

  const handleModeChange = useHandler((mode: Step.RepairFeeMode) => {
    setState((state) =>
      produce(state, (state) => {
        if (state.option.repairFee.strategy !== mode) {
          state.option.repairFee = defaultConfigForMode(mode);
        }
      }),
    );
  });

  const handleAmountChange = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        if (state.option.repairFee.strategy === Step.RepairFeeMode.Instant) {
          state.option.repairFee.amount = parseFloat(value) || 0;
        }
      }),
    );
  });

  const handleMinPriceChange = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        if (state.option.repairFee.strategy === Step.RepairFeeMode.Deferred) {
          state.option.repairFee.minPrice = parseFloat(value) || 0;
        }
      }),
    );
  });

  const handleMaxPriceChange = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        if (state.option.repairFee.strategy === Step.RepairFeeMode.Deferred) {
          state.option.repairFee.maxPrice = parseFloat(value) || 0;
        }
      }),
    );
  });

  const handleAmountTypeChange = useHandler((type: AmountType) => {
    setState((state) =>
      produce(state, (state) => {
        if (state.option.repairFee.strategy !== Step.RepairFeeMode.Free) {
          state.option.repairFee.amountType =
            type === "percentage"
              ? Step.RepairFeeAmountType.Percentage
              : Step.RepairFeeAmountType.Fixed;
        }
      }),
    );
  });

  const repairFee = state.option.repairFee;
  const amountType: AmountType =
    repairFee.strategy !== Step.RepairFeeMode.Free &&
    repairFee.amountType === Step.RepairFeeAmountType.Percentage
      ? "percentage"
      : "fixed";
  const amountTypeDropdown = (
    <AmountTypeDropdown
      amountType={amountType}
      disabled={!canEditReturnsSettings}
      setAmountType={handleAmountTypeChange}
    />
  );

  return (
    <>
      <RedoToggle
        description={t(
          "Merchant pays for labels not covered by {{brandName}}.",
          { brandName: "Redo" },
        )}
        disabled={!canEditReturnsSettings}
        label={t("Safety net")}
        setValue={handleSafetyNet}
        value={state.option.labelPayer === Payer.Merchant}
      />
      <Flex dir="column" gap="md">
        <RedoSingleSelectDropdownInput
          disabled={!canEditReturnsSettings}
          label={t("Repair fee")}
          options={repairFeeModeItems}
          optionSelected={({ item }) => handleModeChange(item.value)}
          selectedItem={repairFeeModeItems.find(
            (item) => item.value === repairFee.strategy,
          )}
          size="xs"
        />
        <Text fontSize="xs" textColor="secondary">
          {repairFeeModeDescription(repairFee.strategy, t)}
        </Text>
        {repairFee.strategy === Step.RepairFeeMode.Instant && (
          <RedoTextInput
            inputTypeHint="number"
            label={t("Fee amount")}
            setValue={handleAmountChange}
            state={!canEditReturnsSettings ? "disabled" : "default"}
            trailingActions={amountTypeDropdown}
            value={String(repairFee.amount)}
          />
        )}
        {repairFee.strategy === Step.RepairFeeMode.Deferred && (
          <Flex dir="row" gap="md">
            <RedoTextInput
              fullWidth
              inputTypeHint="number"
              label={t("Minimum fee")}
              setValue={handleMinPriceChange}
              state={!canEditReturnsSettings ? "disabled" : "default"}
              trailingActions={amountTypeDropdown}
              value={String(repairFee.minPrice)}
            />
            <RedoTextInput
              fullWidth
              inputTypeHint="number"
              label={t("Maximum fee")}
              setValue={handleMaxPriceChange}
              state={!canEditReturnsSettings ? "disabled" : "default"}
              trailingActions={amountTypeDropdown}
              value={String(repairFee.maxPrice)}
            />
          </Flex>
        )}
        {repairFee.strategy !== Step.RepairFeeMode.Free &&
          amountType === "percentage" && (
            <Text fontSize="xs" textColor="secondary">
              {t(
                "Percentage fees are calculated from the item's pre-discount price.",
              )}
            </Text>
          )}
      </Flex>
    </>
  );
});
